import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, IconButton, Button, Tooltip,
  LinearProgress, Fade, Grow, Snackbar, Alert, alpha, useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useApi } from '../../hooks/useApi';
import useResource from '../Customer/components/useResource';
import Section from '../Customer/components/Section';
import DataTable from '../Customer/components/DataTable';
import {
  daysUntil, fmtDate, manufacturerOf, modelOf, serialOf, skuOf, typeOf,
  warrantyDateOf, warrantyStatus,
} from './warrantyStatus';

function DateChip({ iso }) {
  if (!iso) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const status = warrantyStatus({ warrantyDate: iso });
  return <Chip size="small" color={status.color} variant="outlined" label={fmtDate(iso)} />;
}

function StatusCell({ device, hasLansweeperId }) {
  const s = warrantyStatus(device, { hasLansweeperId });
  if (s.kind === 'date') {
    return <Chip size="small" color={s.color} variant="outlined" label={s.label} />;
  }
  return <Typography variant="body2" color="text.secondary">{s.label}</Typography>;
}

const WARRANTY_PRODUCER = 'cma/audit/producers/warranty/Update-WarrantyAudit.ps1';

/**
 * Per-customer Warranty Center. One Datto call — GET datto/site/{code}/devices —
 * already carries hostname, deviceType, udf31–33, and warrantyDate.
 *
 * Refresh warranties queues the producer via POST ves/job and returns immediately.
 */
function Detail() {
  const { code } = useParams();
  const theme = useTheme();
  const jApi = useApi();

  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const [queueing, setQueueing] = useState(false);
  const [toast, setToast] = useState(null);
  const refresh = () => { setRefreshKey(k => k + 1); setLastRefreshed(new Date()); };

  const queueLookup = async () => {
    try {
      setQueueing(true);
      const body = {
        functionpath: WARRANTY_PRODUCER,
        splat: { Account: code },
      };
      const debugQueue = process.env.REACT_APP_VES_QUEUE;
      if (debugQueue) body.queue = debugQueue;
      const resp = await jApi.request('POST', 'ves/job', body);
      const jobId = resp?.data?.id;
      setToast({
        severity: 'success',
        text: jobId
          ? `Warranty lookup queued (job ${jobId}${debugQueue ? `, queue ${debugQueue}` : ''}). Reload from Datto when it finishes.`
          : 'Warranty lookup queued. Reload from Datto when it finishes.',
      });
    } catch (ex) {
      setToast({
        severity: 'error',
        text: ex?.response?.data?.error?.message || ex?.toString?.() || 'Failed to queue warranty lookup',
      });
    } finally {
      setQueueing(false);
    }
  };

  const accountRes = useResource(`cma/account?code=${encodeURIComponent(code)}`, { refreshKey });
  const account = useMemo(() => {
    const list = Array.isArray(accountRes.data) ? accountRes.data : [];
    return list.find(a => a.code === code) || list[0] || null;
  }, [accountRes.data, code]);

  const customerName = account?.name || code;
  const hasDatto = Boolean(account?.datto_id);
  const hasLansweeperId = Boolean(account?.lansweeper_id);

  const devicesRes = useResource(
    `datto/site/${encodeURIComponent(code)}/devices`,
    { refreshKey, enabled: hasDatto }
  );
  const devices = useMemo(
    () => (Array.isArray(devicesRes.data) ? devicesRes.data : []),
    [devicesRes.data]
  );

  const summary = useMemo(() => {
    let expired = 0, soon = 0, amber = 0, current = 0, missing = 0;
    for (const d of devices) {
      const date = warrantyDateOf(d);
      if (!date) { missing += 1; continue; }
      const days = daysUntil(date);
      if (days === null) missing += 1;
      else if (days < 0) expired += 1;
      else if (days < 30) soon += 1;
      else if (days < 90) amber += 1;
      else current += 1;
    }
    return { expired, soon, amber, current, missing, total: devices.length };
  }, [devices]);

  const columns = useMemo(() => {
    // MRT/TanStack default is alphanumeric (leading digits compared as numbers),
    // which puts 59QFQM3 after 9SYVMC4. Serials and hostnames need dictionary order.
    const textCol = { sortingFn: 'text' };
    return [
    { accessorKey: 'hostname', header: 'Hostname', size: 160, ...textCol },
    {
      id: 'type', header: 'Type', size: 110,
      accessorFn: r => typeOf(r) || '—',
      ...textCol,
    },
    {
      id: 'manufacturer', header: 'Manufacturer', size: 140,
      accessorFn: r => manufacturerOf(r) || '—',
      ...textCol,
    },
    {
      id: 'model', header: 'Model', size: 180,
      accessorFn: r => modelOf(r) || '—',
      ...textCol,
    },
    {
      id: 'serial', header: 'Serial', size: 140,
      accessorFn: r => serialOf(r) || '—',
      ...textCol,
    },
    {
      id: 'sku', header: 'Product SKU', size: 140,
      accessorFn: r => skuOf(r) || '—',
      ...textCol,
    },
    {
      id: 'warrantyDate', header: 'Warranty Expiration', size: 170,
      accessorFn: r => {
        const raw = warrantyDateOf(r);
        return raw ? String(raw).slice(0, 10) : undefined;
      },
      sortUndefined: 'last',
      Cell: ({ row }) => <DateChip iso={warrantyDateOf(row.original)} />,
    },
    {
      id: 'status', header: 'Status', size: 260,
      accessorFn: r => warrantyStatus(r, { hasLansweeperId }).label,
      Cell: ({ row }) => <StatusCell device={row.original} hasLansweeperId={hasLansweeperId} />,
      ...textCol,
    },
  ];
  }, [hasLansweeperId]);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  const loading = accountRes.loading || (hasDatto && devicesRes.loading);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/warranty" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Warranty Center</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{customerName}</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {customerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Account: {code}
                  {hasDatto ? '' : ' · no Datto site'}
                  {hasLansweeperId ? ' · Lansweeper linked' : ' · Lansweeper not linked'}
                </Typography>
              </Box>

              {hasDatto && devices.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="error" variant="outlined" label={`${summary.expired} expired`} />
                  <Chip size="small" color="error" variant="outlined" label={`${summary.soon} < 30d`} />
                  <Chip size="small" color="warning" variant="outlined" label={`${summary.amber} < 90d`} />
                  <Chip size="small" color="success" variant="outlined" label={`${summary.current} current`} />
                  <Chip size="small" variant="outlined" label={`${summary.missing} no date`} />
                </Stack>
              )}

              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={hasDatto
                  ? 'Queue a Lansweeper warranty lookup for this account. Does not wait — reload from Datto after it finishes.'
                  : 'No Datto site is linked to this account.'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasDatto || queueing}
                      onClick={queueLookup}
                    >
                      {queueing ? 'Queueing…' : 'Refresh warranties'}
                    </Button>
                  </span>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Updated {lastRefreshed.toLocaleTimeString()}
                </Typography>
                <IconButton size="small" onClick={refresh} disabled={loading} title="Reload from Datto">
                  <RefreshIcon/>
                </IconButton>
              </Stack>
            </Stack>
            {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {accountRes.error && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">
              Could not load account {code} — {accountRes.error}
            </Typography>
          </Paper>
        )}

        {!accountRes.loading && !hasDatto && !accountRes.error && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No Datto RMM site is linked to this account, so there is no equipment list to show.
            </Typography>
          </Paper>
        )}

        {hasDatto && (
          <Box sx={{ py: 3 }}>
            <Section
              title="Equipment"
              count={summary.total}
              loading={devicesRes.loading}
              error={devicesRes.error}
            >
              <DataTable
                columns={columns}
                data={devices}
                isLoading={devicesRes.loading}
                emptyText="No Datto devices for this account."
                getRowId={r => r.uid || r.hostname}
                enableTopToolbar
                enableGlobalFilter
                initialState={{
                  sorting: [{ id: 'warrantyDate', desc: false }],
                  pagination: { pageSize: 25, pageIndex: 0 },
                }}
                muiTableContainerProps={{ sx: { maxHeight: '70vh' } }}
              />
            </Section>
          </Box>
        )}
      </Container>
      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default Detail;
